import {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  doc,
  getDoc,
  setDoc
} from "../access/firestore";

import {
  db
} from "../firebase";

import AnatomicalTooth from "../components/dental/AnatomicalTooth";

import {
  evaluarRiesgoPeriodontal,
  generarAlertasMedicas,
  clasificarPeriodonto
} from "../utils/ClinicalEngine";

import {
  sugerirTratamientoPeriodontal
} from "../utils/TreatmentEngine";

import {
  generarSOAPPeriodontal
} from "../utils/SOAPEngine";


// ======================================================
// DIENTES
// ======================================================

const DIENTES_SUPERIORES = [
  "18","17","16","15","14","13","12","11",
  "21","22","23","24","25","26","27","28"
];

const DIENTES_INFERIORES = [
  "48","47","46","45","44","43","42","41",
  "31","32","33","34","35","36","37","38"
];


// ======================================================
// SITIOS PERIODONTALES
// ======================================================

const SITIOS = [
  "MB",
  "B",
  "DB",
  "ML",
  "L",
  "DL"
];


// ======================================================
// CUADRANTES
// ======================================================

const CUADRANTES = {

  "Superior derecho": [
    "18","17","16","15","14","13","12","11"
  ],

  "Superior izquierdo": [
    "21","22","23","24","25","26","27","28"
  ],

  "Inferior izquierdo": [
    "31","32","33","34","35","36","37","38"
  ],

  "Inferior derecho": [
    "48","47","46","45","44","43","42","41"
  ]

};


// ======================================================
// COLORES
// ======================================================

function colorProfundidad(valor) {

  const numero =
    Number(valor);

  if (!numero) {
    return "#ffffff";
  }

  if (numero >= 6) {
    return "#fecaca";
  }

  if (numero >= 4) {
    return "#fef3c7";
  }

  return "#dcfce7";

}

function colorArcada(datos, diente, sitios) {
  const valores = sitios
    .map((sitio) => Number(datos[`${diente}_${sitio}`]))
    .filter((valor) => valor > 0);

  if (!valores.length) return "#ffffff";

  return colorProfundidad(Math.max(...valores));
}

function coloresDiente(datos, diente) {
  return {
    vestibular: colorArcada(datos, diente, ["MB", "B", "DB"]),
    lingual: colorArcada(datos, diente, ["ML", "L", "DL"]),
  };
}


// ======================================================
// BOTÓN PRINCIPAL
// ======================================================

function botonBase(activo) {

  return {

    flex: 1,

    minHeight: 52,

    borderRadius: 14,

    border: activo
      ? "2px solid #2563eb"
      : "1px solid #e2e8f0",

    background: activo
      ? "#eff6ff"
      : "#ffffff",

    color: activo
      ? "#2563eb"
      : "#475569",

    fontSize: 14,

    fontWeight: 700,

    cursor: "pointer"

  };

}


// ======================================================
// COMPONENTE
// ======================================================

export default function MobilePeriodontograma({

  pacienteId,

  clinicId

}) {


  // ====================================================
  // ESTADOS
  // ====================================================

  const [
    datos,
    setDatos
  ] = useState({});


  const [
    bop,
    setBop
  ] = useState({});


  const [
    movilidad,
    setMovilidad
  ] = useState({});


  const [
    furca,
    setFurca
  ] = useState({});

  const [
    pacienteData,
    setPacienteData
  ] = useState({});

  const [
    historiaClinica,
    setHistoriaClinica
  ] = useState({});


  const [
    loading,
    setLoading
  ] = useState(true);


  const [
    guardando,
    setGuardando
  ] = useState(false);


  const [
    cuadrante,
    setCuadrante
  ] = useState(
    "Superior derecho"
  );


  const [
    dienteSeleccionado,
    setDienteSeleccionado
  ] = useState(null);


  // ====================================================
  // CARGAR PERIODONTOGRAMA
  // ====================================================

  useEffect(() => {

    if (
      !clinicId ||
      !pacienteId
    ) {

      setLoading(false);

      return;

    }


    const cargar =
      async () => {

        try {

          const referencia =
            doc(
              db,
              "clinics",
              clinicId,
              "pacientes",
              pacienteId
            );


          const snapshot =
            await getDoc(
              referencia
            );


          if (
            snapshot.exists()
          ) {

            const data =
              snapshot.data();

            setPacienteData(data);
            setHistoriaClinica(
              data?.historiaClinica || {}
            );


            const periodonto =
              data?.periodonto ||
              {};


            setDatos(
              periodonto.datos ||
              {}
            );


            setBop(
              periodonto.bop ||
              {}
            );


            setMovilidad(
              periodonto.movilidad ||
              {}
            );


            setFurca(
              periodonto.furca ||
              {}
            );

          }

        } catch (error) {

          console.error(
            "Error cargando periodontograma:",
            error
          );

        } finally {

          setLoading(false);

        }

      };


    cargar();

  }, [
    clinicId,
    pacienteId
  ]);


  // ====================================================
  // DIENTES DEL CUADRANTE
  // ====================================================

  const dientes =
    useMemo(() => {

      return (
        CUADRANTES[
          cuadrante
        ] || []
      );

    }, [
      cuadrante
    ]);


  // ====================================================
  // MODIFICAR PROFUNDIDAD
  // ====================================================

  const cambiarProfundidad = (
    diente,
    sitio,
    valor
  ) => {

    const key =
      `${diente}_${sitio}`;


    setDatos(
      anterior => ({

        ...anterior,

        [key]: valor

      })
    );

  };


  // ====================================================
  // BOP
  // ====================================================

  const alternarBOP = (
    diente,
    sitio
  ) => {

    const key =
      `${diente}_${sitio}`;


    setBop(
      anterior => ({

        ...anterior,

        [key]:
          !anterior[key]

      })
    );

  };


  // ====================================================
  // GUARDAR
  // ====================================================

  const guardar = async () => {

    if (
      !clinicId ||
      !pacienteId
    ) {

      return;

    }


    try {

      setGuardando(true);


      await setDoc(

        doc(
          db,
          "clinics",
          clinicId,
          "pacientes",
          pacienteId
        ),

        {

          periodonto: {

            datos,

            bop,

            movilidad,

            furca

          }

        },

        {
          merge: true
        }

      );

      setGuardando(false);

    } catch (error) {

      console.error(
        "Error guardando periodontograma:",
        error
      );

      setGuardando(false);

    }

  };


  // ====================================================
  // ANALISIS
  // ====================================================

  const resultado =
    useMemo(() => {

      const valores =
        Object.values(
          datos
        )

          .map(
            valor =>
              Number(valor)
          )

          .filter(
            valor =>
              valor > 0
          );


      if (
        !valores.length
      ) {

        return null;

      }


      const promedio =
        valores.reduce(
          (
            acumulado,
            valor
          ) =>
            acumulado + valor,
          0
        ) /
        valores.length;


      const max =
        Math.max(
          ...valores
        );


      const mayoresIgual4 =
        valores.filter(
          valor =>
            valor >= 4
        ).length;


      const porcentaje =
        (
          mayoresIgual4 /
          valores.length
        ) *
        100;


      const bopTotal =
        Object.values(
          bop
        ).filter(
          valor =>
            valor
        ).length;


      const bopPorcentaje =
        (
          bopTotal /
          valores.length
        ) *
        100;


      const movMax =
        Math.max(

          ...Object.values(
            movilidad
          ).map(
            valor =>
              Number(valor) || 0
          ),

          0

        );


      const furcaMax =
        Math.max(

          ...Object.values(
            furca
          ).map(
            valor =>
              Number(valor) || 0
          ),

          0

        );


      return {

        promedio:
          promedio.toFixed(2),

        max,

        porcentaje:
          porcentaje.toFixed(1),

        bop:
          bopPorcentaje.toFixed(1),

        movMax,

        furcaMax

      };

    }, [

      datos,

      bop,

      movilidad,

      furca

    ]);

  const riesgo = evaluarRiesgoPeriodontal({
    paciente: pacienteData,
    historiaClinica,
    periodonto: {
      datos,
      bop,
      movilidad,
      furca,
    },
  });

  const alertas = generarAlertasMedicas({
    historiaClinica,
  });

  const clasificacion = clasificarPeriodonto({
    periodonto: { datos },
  });

  const tratamiento = sugerirTratamientoPeriodontal({
    clasificacion,
    riesgo,
    historiaClinica,
  });

  const soap = generarSOAPPeriodontal({
    paciente: pacienteData,
    historiaClinica,
    clasificacion,
    riesgo,
    tratamiento,
    periodonto: {
      datos,
      bop,
      movilidad,
      furca,
    },
  });


  // ====================================================
  // CARGANDO
  // ====================================================

  if (loading) {

    return (

      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: "#64748b"
        }}
      >

        Cargando periodontograma...

      </div>

    );

  }


  // ====================================================
  // RENDER
  // ====================================================

  return (

    <div
      style={{

        width: "100%",

        boxSizing:
          "border-box",

        padding: 12,

        paddingBottom: 110

      }}
    >


      {/* ============================================== */}
      {/* ENCABEZADO */}
      {/* ============================================== */}

      <div
        style={{
          marginBottom: 18
        }}
      >

        <div
          style={{

            fontSize: 24,

            fontWeight: 800,

            color: "#111827"

          }}
        >

          Periodontograma

        </div>


        <div
          style={{

            marginTop: 5,

            fontSize: 14,

            lineHeight: 1.45,

            color: "#64748b"

          }}
        >

          Selecciona un cuadrante y
          después el diente que deseas
          registrar.

        </div>

      </div>


      {/* ============================================== */}
      {/* CUADRANTES */}
      {/* ============================================== */}

      <div
        style={{

          display: "grid",

          gridTemplateColumns:
            "1fr 1fr",

          gap: 8,

          marginBottom: 16

        }}
      >

        {Object.keys(
          CUADRANTES
        ).map(
          nombre => (

            <button

              key={nombre}

              type="button"

              onClick={() =>
                setCuadrante(
                  nombre
                )
              }

              style={botonBase(
                cuadrante ===
                nombre
              )}

            >

              {nombre}

            </button>

          )
        )}

      </div>


      {/* ============================================== */}
      {/* DIENTES */}
      {/* ============================================== */}

      <div
        style={{

          background: "#ffffff",

          border:
            "1px solid #e2e8f0",

          borderRadius: 18,

          padding: 14,

          marginBottom: 18,

          boxShadow:
            "0 4px 14px rgba(15,23,42,0.05)"

        }}
      >

        <div
          style={{

            display: "flex",

            alignItems:
              "center",

            justifyContent:
              "space-between",

            marginBottom: 12

          }}
        >

          <div
            style={{

              fontSize: 17,

              fontWeight: 800,

              color: "#111827"

            }}
          >

            {cuadrante}

          </div>


          <div
            style={{

              fontSize: 13,

              color: "#64748b"

            }}
          >

            {dientes.length} dientes · {DIENTES_SUPERIORES.length + DIENTES_INFERIORES.length} en ambas arcadas

          </div>

        </div>


        <div
          style={{

            display: "grid",

            gridTemplateColumns:
              "repeat(4, 1fr)",

            gap: 8

          }}
        >

          {dientes.map(
            diente => {

              const tieneDatos =
                SITIOS.some(
                  sitio =>
                    Number(
                      datos[
                        `${diente}_${sitio}`
                      ]
                    ) > 0
                );


              const tieneBOP =
                SITIOS.some(
                  sitio =>
                    bop[
                      `${diente}_${sitio}`
                    ]
                );


              const activo =
                dienteSeleccionado ===
                diente;


              return (

                <button

                  key={diente}

                  type="button"

                  aria-label={`Registrar periodonto del diente ${diente}`}

                  onClick={() =>
                    setDienteSeleccionado(
                      diente
                    )
                  }

                  style={{

                    position:
                      "relative",

                    minHeight: 86,

                    padding: "4px 2px 2px",

                    borderRadius: 14,

                    border:
                      activo
                        ? "2px solid #2563eb"
                        : "1px solid #e2e8f0",

                    background:
                      activo
                        ? "#eff6ff"
                        : "#ffffff",

                    color:
                      activo
                        ? "#2563eb"
                        : "#334155",

                    fontSize: 16,

                    fontWeight: 800,

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center"

                  }}

                >

                  <AnatomicalTooth
                    numero={diente}
                    compact
                    selected={activo}
                    colors={coloresDiente(datos, diente)}
                  />


                  {tieneDatos && (

                    <span
                      style={{

                        position:
                          "absolute",

                        top: 6,

                        right: 6,

                        width: 7,

                        height: 7,

                        borderRadius:
                          "50%",

                        background:
                          "#2563eb"

                      }}
                    />

                  )}


                  {tieneBOP && (

                    <span
                      style={{

                        position:
                          "absolute",

                        bottom: 6,

                        right: 6,

                        width: 7,

                        height: 7,

                        borderRadius:
                          "50%",

                        background:
                          "#dc2626"

                      }}
                    />

                  )}

                </button>

              );

            }
          )}

        </div>

      </div>


      {/* ============================================== */}
      {/* EDITOR DEL DIENTE */}
      {/* ============================================== */}

      {dienteSeleccionado && (

        <div
          style={{

            background: "#ffffff",

            border:
              "2px solid #2563eb",

            borderRadius: 18,

            padding: 16,

            marginBottom: 18,

            boxShadow:
              "0 8px 24px rgba(37,99,235,0.10)"

          }}
        >


          <div
            style={{

              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "center",

              marginBottom: 16

            }}
          >

            <div>

              <div
                style={{

                  fontSize: 13,

                  color: "#64748b",

                  fontWeight: 700

                }}
              >

                Diente seleccionado

              </div>


              <div
                style={{

                  marginTop: 2,

                  fontSize: 30,

                  fontWeight: 800,

                  color: "#2563eb"

                }}
              >

                {dienteSeleccionado}

              </div>

            </div>


            <button

              type="button"

              onClick={() =>
                setDienteSeleccionado(
                  null
                )
              }

              style={{

                width: 42,

                height: 42,

                borderRadius: 12,

                border: "none",

                background:
                  "#f1f5f9",

                color: "#475569",

                fontSize: 22,

                fontWeight: 700

              }}

            >

              ×

            </button>

          </div>


          {/* ========================================== */}
          {/* PROFUNDIDADES */}
          {/* ========================================== */}

          <div
            style={{

              fontSize: 16,

              fontWeight: 800,

              color: "#334155",

              marginBottom: 10

            }}
          >

            Profundidad de sondaje

          </div>


          <div
            style={{

              display: "grid",

              gridTemplateColumns:
                "repeat(3, 1fr)",

              gap: 8

            }}
          >

            {SITIOS.map(
              sitio => {

                const key =
                  `${dienteSeleccionado}_${sitio}`;

                const valor =
                  datos[key] ||
                  "";


                return (

                  <div
                    key={sitio}
                  >

                    <div
                      style={{

                        fontSize: 11,

                        fontWeight: 800,

                        color: "#64748b",

                        textAlign:
                          "center",

                        marginBottom: 5

                      }}
                    >

                      {sitio}

                    </div>


                    <input

                      type="number"

                      min="0"

                      max="20"

                      inputMode="numeric"

                      value={valor}

                      onChange={event =>
                        cambiarProfundidad(

                          dienteSeleccionado,

                          sitio,

                          event.target.value

                        )
                      }

                      style={{

                        width: "100%",

                        boxSizing:
                          "border-box",

                        height: 48,

                        borderRadius: 12,

                        border:
                          "1px solid #cbd5e1",

                        background:
                          colorProfundidad(
                            valor
                          ),

                        textAlign:
                          "center",

                        fontSize: 18,

                        fontWeight: 700,

                        color: "#1e293b",

                        outline: "none"

                      }}

                    />

                  </div>

                );

              }
            )}

          </div>


          {/* ========================================== */}
          {/* BOP */}
          {/* ========================================== */}

          <div
            style={{

              marginTop: 22,

              paddingTop: 18,

              borderTop:
                "1px solid #e2e8f0"

            }}
          >

            <div
              style={{

                fontSize: 16,

                fontWeight: 800,

                color: "#334155",

                marginBottom: 4

              }}
            >

              Sangrado al sondaje (BOP)

            </div>


            <div
              style={{

                fontSize: 12,

                color: "#64748b",

                marginBottom: 10

              }}
            >

              Toca cada sitio para marcar o
              quitar el sangrado.

            </div>


            <div
              style={{

                display: "grid",

                gridTemplateColumns:
                  "repeat(3, 1fr)",

                gap: 8

              }}
            >

              {SITIOS.map(
                sitio => {

                  const key =
                    `${dienteSeleccionado}_${sitio}`;

                  const activo =
                    !!bop[key];


                  return (

                    <button

                      key={sitio}

                      type="button"

                      onClick={() =>
                        alternarBOP(

                          dienteSeleccionado,

                          sitio

                        )
                      }

                      style={{

                        minHeight: 44,

                        borderRadius: 12,

                        border:
                          activo
                            ? "2px solid #dc2626"
                            : "1px solid #e2e8f0",

                        background:
                          activo
                            ? "#fee2e2"
                            : "#ffffff",

                        color:
                          activo
                            ? "#b91c1c"
                            : "#475569",

                        fontSize: 13,

                        fontWeight: 800

                      }}

                    >

                      {sitio}

                    </button>

                  );

                }
              )}

            </div>

          </div>


          {/* ========================================== */}
          {/* MOVILIDAD */}
          {/* ========================================== */}

          <div
            style={{

              marginTop: 22,

              paddingTop: 18,

              borderTop:
                "1px solid #e2e8f0"

            }}
          >

            <div
              style={{

                fontSize: 16,

                fontWeight: 800,

                color: "#334155",

                marginBottom: 9

              }}
            >

              Movilidad

            </div>


            <select

              value={
                movilidad[
                  dienteSeleccionado
                ] || ""
              }

              onChange={event =>
                setMovilidad(
                  anterior => ({

                    ...anterior,

                    [dienteSeleccionado]:
                      event.target.value

                  })
                )
              }

              style={{

                width: "100%",

                height: 48,

                borderRadius: 12,

                border:
                  "1px solid #cbd5e1",

                background: "#ffffff",

                padding:
                  "0 12px",

                fontSize: 15,

                color: "#334155"

              }}

            >

              <option value="">
                Sin registrar
              </option>

              <option value="0">
                0 — Normal
              </option>

              <option value="1">
                I
              </option>

              <option value="2">
                II
              </option>

              <option value="3">
                III
              </option>

            </select>

          </div>


          {/* ========================================== */}
          {/* FURCA */}
          {/* ========================================== */}

          <div
            style={{

              marginTop: 16

            }}
          >

            <div
              style={{

                fontSize: 16,

                fontWeight: 800,

                color: "#334155",

                marginBottom: 9

              }}
            >

              Furca

            </div>


            <select

              value={
                furca[
                  dienteSeleccionado
                ] || ""
              }

              onChange={event =>
                setFurca(
                  anterior => ({

                    ...anterior,

                    [dienteSeleccionado]:
                      event.target.value

                  })
                )
              }

              style={{

                width: "100%",

                height: 48,

                borderRadius: 12,

                border:
                  "1px solid #cbd5e1",

                background: "#ffffff",

                padding:
                  "0 12px",

                fontSize: 15,

                color: "#334155"

              }}

            >

              <option value="">
                Sin registrar
              </option>

              <option value="0">
                0
              </option>

              <option value="1">
                I
              </option>

              <option value="2">
                II
              </option>

              <option value="3">
                III
              </option>

            </select>

          </div>


          {/* ========================================== */}
          {/* GUARDAR */}
          {/* ========================================== */}

          <button

            type="button"

            onClick={guardar}

            disabled={guardando}

            style={{

              width: "100%",

              marginTop: 22,

              minHeight: 52,

              borderRadius: 14,

              border: "none",

              background:
                guardando
                  ? "#93c5fd"
                  : "#2563eb",

              color: "#ffffff",

              fontSize: 16,

              fontWeight: 800

            }}

          >

            {guardando
              ? "Guardando..."
              : "Guardar periodontograma"}

          </button>


        </div>

      )}


      {/* ============================================== */}
      {/* ANÁLISIS */}
      {/* ============================================== */}

      {resultado && (

        <div
          style={{

            background: "#ffffff",

            border:
              "1px solid #e2e8f0",

            borderRadius: 18,

            padding: 16,

            marginBottom: 18

          }}
        >

          <div
            style={{

              fontSize: 18,

              fontWeight: 800,

              color: "#111827",

              marginBottom: 14

            }}
          >

            Análisis periodontal

          </div>


          <div
            style={{

              display: "grid",

              gridTemplateColumns:
                "1fr 1fr",

              gap: 10

            }}
          >

            <div
              style={{

                padding: 12,

                borderRadius: 12,

                background: "#f8fafc"

              }}
            >

              <div
                style={{

                  fontSize: 12,

                  color: "#64748b"

                }}
              >

                Promedio

              </div>

              <strong
                style={{
                  fontSize: 18
                }}
              >

                {resultado.promedio} mm

              </strong>

            </div>


            <div
              style={{

                padding: 12,

                borderRadius: 12,

                background: "#f8fafc"

              }}
            >

              <div
                style={{

                  fontSize: 12,

                  color: "#64748b"

                }}
              >

                Máximo

              </div>

              <strong
                style={{
                  fontSize: 18
                }}
              >

                {resultado.max} mm

              </strong>

            </div>


            <div
              style={{

                padding: 12,

                borderRadius: 12,

                background: "#f8fafc"

              }}
            >

              <div
                style={{

                  fontSize: 12,

                  color: "#64748b"

                }}
              >

                ≥ 4 mm

              </div>

              <strong
                style={{
                  fontSize: 18
                }}
              >

                {resultado.porcentaje}%

              </strong>

            </div>


            <div
              style={{

                padding: 12,

                borderRadius: 12,

                background: "#f8fafc"

              }}
            >

              <div
                style={{

                  fontSize: 12,

                  color: "#64748b"

                }}
              >

                BOP

              </div>

              <strong
                style={{
                  fontSize: 18
                }}
              >

                {resultado.bop}%

              </strong>

            </div>


            <div
              style={{

                padding: 12,

                borderRadius: 12,

                background: "#f8fafc"

              }}
            >

              <div
                style={{

                  fontSize: 12,

                  color: "#64748b"

                }}
              >

                Movilidad máxima

              </div>

              <strong
                style={{
                  fontSize: 18
                }}
              >

                {resultado.movMax}

              </strong>

            </div>


            <div
              style={{

                padding: 12,

                borderRadius: 12,

                background: "#f8fafc"

              }}
            >

              <div
                style={{

                  fontSize: 12,

                  color: "#64748b"

                }}
              >

                Furca máxima

              </div>

              <strong
                style={{
                  fontSize: 18
                }}
              >

                {resultado.furcaMax}

              </strong>

            </div>

          </div>

        </div>

      )}

      {resultado && (
        <>
          <section style={{ background: "linear-gradient(135deg, #10243e, #174a72)", borderRadius: 18, padding: 16, marginBottom: 18, color: "#ffffff" }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#bfdbfe" }}>
              Lectura clínica
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginTop: 8 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 850 }}>{clasificacion.dx}</div>
                <div style={{ marginTop: 5, color: "#dbeafe", fontSize: 12 }}>
                  Severidad: {clasificacion.severidad || "pendiente"}
                </div>
              </div>
              <div style={{ padding: "8px 10px", borderRadius: 11, background: "rgba(255,255,255,.14)", color: "#ffffff", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" }}>
                Riesgo {riesgo.riesgo}
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
              {(riesgo.factores?.length ? riesgo.factores : ["Sin factores adicionales registrados"]).map((factor) => (
                <span key={factor} style={{ padding: "6px 8px", borderRadius: 8, background: "rgba(255,255,255,.12)", color: "#e0f2fe", fontSize: 11 }}>
                  {factor}
                </span>
              ))}
            </div>
          </section>

          {alertas.length > 0 && (
            <section style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 16, padding: 15, marginBottom: 18 }}>
              <div style={{ fontSize: 16, fontWeight: 850, color: "#9a3412", marginBottom: 10 }}>Alertas clínicas</div>
              {alertas.map((alerta, index) => (
                <div key={`${alerta.titulo}-${index}`} style={{ padding: "9px 0", borderTop: index ? "1px solid #fed7aa" : "none", color: "#7c2d12", fontSize: 12, lineHeight: 1.5 }}>
                  <strong>{alerta.titulo}</strong> · {alerta.descripcion}
                </div>
              ))}
            </section>
          )}

          <section style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 18, padding: 16, marginBottom: 18 }}>
            <div style={{ fontSize: 18, fontWeight: 850, color: "#111827" }}>Plan de tratamiento sugerido</div>
            <p style={{ color: "#64748b", fontSize: 12, lineHeight: 1.55, margin: "6px 0 14px" }}>
              Propuesta basada en los datos registrados. Debe validarse clínicamente antes de aplicarse.
            </p>
            {tratamiento.plan.map((fase, index) => (
              <div key={fase.fase} style={{ display: "flex", gap: 10, padding: "12px 0", borderTop: index ? "1px solid #eef2f7" : "none" }}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, flexShrink: 0, borderRadius: 9, background: "#eff6ff", color: "#2563eb", fontWeight: 900 }}>
                  {index + 1}
                </span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 850, color: "#172033" }}>{fase.fase}</div>
                  <div style={{ marginTop: 5, color: "#475569", fontSize: 12, lineHeight: 1.55 }}>{fase.procedimientos.join(" · ")}</div>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 10, padding: 11, borderRadius: 11, background: "#f8fafc", color: "#475569", fontSize: 12 }}>
              Mantenimiento sugerido: <strong>{tratamiento.mantenimiento}</strong>
            </div>
          </section>

          <section style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 18, padding: 16, marginBottom: 18 }}>
            <div style={{ fontSize: 18, fontWeight: 850, color: "#111827" }}>Nota clínica SOAP</div>
            <p style={{ color: "#64748b", fontSize: 12, lineHeight: 1.55, margin: "6px 0 14px" }}>
              Resumen generado para revisar, editar y trasladar a la evolución clínica.
            </p>
            <div style={{ display: "grid", gap: 10 }}>
              {[
                ["S", "Subjetivo", soap.subjetivo, "#eff6ff", "#2563eb"],
                ["O", "Objetivo", soap.objetivo, "#ecfeff", "#0891b2"],
                ["A", "Análisis", soap.analisis, "#f5f3ff", "#7c3aed"],
                ["P", "Plan", soap.plan, "#ecfdf5", "#059669"],
              ].map(([letra, titulo, items, fondo, color]) => (
                <div key={letra} style={{ padding: 12, borderRadius: 12, background: fondo, border: `1px solid ${color}22` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color, fontWeight: 850, fontSize: 13 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 25, height: 25, borderRadius: 8, background: "#ffffffaa", fontWeight: 900 }}>{letra}</span>
                    {titulo}
                  </div>
                  <div style={{ marginTop: 8, display: "grid", gap: 5, color: "#475569", fontSize: 12, lineHeight: 1.55 }}>
                    {(items.length ? items : ["Sin información registrada."]).map((item, index) => (
                      <div key={`${letra}-${index}`}>{item}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

    </div>

  );

}
